'use client';

import { API_ROUTES, useApiInvalidate } from '@shared/api-utils';
import { useSignpostingNotifications } from '@shared/providers';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { FilterButton } from '../../../filters/components/FilterButton/FilterButton';
import { FilterWithChips } from '../../../filters/components/FilterWithChips/FilterWithChips';
import { ContentSwitcher } from '../../../form-controls/ContentSwitcher/ContentSwitcher';
import type { Option } from '../../../form-controls/types';
import { HorizontalScrollContainer } from '../../../HorizontalScrollContainer/HorizontalScrollContainer';
import { SecondaryHeaderClient } from '../../../SecondaryHeaderContainer/components/SecondaryHeaderClient/SecondaryHeaderClient';
import { Tag } from '../../../Tag/Tag';
import { PromoContentBonusFunds } from '../../PromoContentBonusFunds/PromoContentBonusFunds';
import { PromoContentFreeSpins } from '../../PromoContentFreeSpins/PromoContentFreeSpins';
import { PromoContentOffers } from '../../PromoContentOffers/PromoContentOffers';
import { PromotionsFilterProvider, usePromotionsFilter } from '../../providers';
import { SportsbookFreeBets } from '../../SportsbookFreeBets/SportsbookFreeBets';
import type { ProductVerticalCategory, PromotionsPageType } from '../../types/types';
import { PromotionsHeader, type PromotionsHeaderVariant } from '../PromotionsHeader/PromotionsHeader';
import { PromotionsLoginOrSignUp } from '../PromotionsLoginOrSignUp/PromotionsLoginOrSignUp';

type PromotionsPageClientProps = {
  options: Option<PromotionsPageType>[];
  headerVariant?: PromotionsHeaderVariant;
  showFilters?: boolean;
  showTabSwitcher?: boolean;
  loginRequiredSections?: PromotionsPageType[];
};

const NOTIFICATION_TYPE_TO_TAB_VALUE: Record<string, PromotionsPageType> = {
  BUNDLE: 'offers',
  BONUS_FUND: 'bonuses',
  FREE_SPIN: 'free-spins',
  FREE_BET: 'free-bets',
};

const TAB_VALUE_TO_NOTIFICATION_TYPE: Record<PromotionsPageType, string> = {
  offers: 'BUNDLE',
  bonuses: 'BONUS_FUND',
  'free-spins': 'FREE_SPIN',
  'free-bets': 'FREE_BET',
};

/**
 * Filters panel component that allows users to filter promotions by vertical.
 * It displays the number of promotions that match the selected filters, a button to clear the filters,
 * a button to show the promotions, and a horizontal scroll container to display the selected filters.
 * @returns The filters panel component.
 */
function FiltersPanel() {
  const t = useTranslations('offers.filters');
  const { chips, selectedFilters, setSelectedFilters, filteredOffers } = usePromotionsFilter();
  const appliedFiltersCount = selectedFilters.length;
  const { ClearButton, ActionsContainer, ShowButton, FiltersContainer } = FilterButton;

  function handleRemoveTag(value: ProductVerticalCategory) {
    setSelectedFilters(selectedFilters.filter(filter => filter !== value));
  }

  return (
    <SecondaryHeaderClient className="ll-container-full py-0">
      <div className="flex items-center justify-between ll-container-inner pt-4 sm:pt-6">
        <p className="paragraph-s-strong text-foreground-subtle min-h-8 flex items-center">{t('results', { count: filteredOffers.length })}</p>
        <FilterButton appliedFiltersCount={appliedFiltersCount} title={t('title')} disabled={chips.length === 0}>
          <FiltersContainer>
            <FilterWithChips<ProductVerticalCategory>
              namespace="offers.filters.by_vertical"
              onValueChange={value => setSelectedFilters(value)}
              chips={chips}
              value={selectedFilters}
              data-track-container="Verticals"
            />
          </FiltersContainer>
          <ActionsContainer>
            <ClearButton disabled={appliedFiltersCount === 0} onClick={() => setSelectedFilters([])}>
              {t('clear')}
            </ClearButton>
            <ShowButton disabled={appliedFiltersCount === 0}>{t('continue')}</ShowButton>
          </ActionsContainer>
        </FilterButton>
      </div>
      {selectedFilters.length > 0 ? (
        <HorizontalScrollContainer role="list" className="ll-container-full md:ll-container-inner px-(--ll-container-padding) md:px-0 mt-4 flex-row-reverse">
          {selectedFilters.map(filter => (
            <Tag key={filter} className="shrink-0" {...chips.find(chip => chip.value === filter)!} onRemove={() => handleRemoveTag(filter)} />
          ))}
        </HorizontalScrollContainer>
      ) : null}
    </SecondaryHeaderClient>
  );
}

function PromotionsPageClient({ headerVariant = 'toolbar', showFilters = false, options, showTabSwitcher = true, loginRequiredSections }: PromotionsPageClientProps) {
  const searchParams = useSearchParams();

  const initialOption = options.find(option => option.value === searchParams.get('tab')) ?? options[0]!;
  const [promoType, setPromoType] = useState<Option<PromotionsPageType>>(initialOption);
  const { hasNotificationsForType, clearMarkedNotificationsForType, hasNotifications } = useSignpostingNotifications();
  const { invalidateQueriesMatchingPath } = useApiInvalidate();
  const hasInvalidatedRef = useRef(false);
  const loginProtectionRequired = loginRequiredSections?.includes(promoType.value) ?? false;

  useEffect(() => {
    if (!hasNotifications || hasInvalidatedRef.current) {
      return;
    }

    hasInvalidatedRef.current = true;

    const bonusFundsPrefix = API_ROUTES.PROMOTIONS.BONUS_FUNDS.split(':')[0] ?? API_ROUTES.PROMOTIONS.BONUS_FUNDS;
    const freeSpinsPrefix = API_ROUTES.PROMOTIONS.FREE_SPINS.split(':')[0] ?? API_ROUTES.PROMOTIONS.FREE_SPINS;

    const offersPublicPrefix = API_ROUTES.PROMOTIONS.OFFERS_AND_REWARDS.PUBLIC.split('?')[0] ?? API_ROUTES.PROMOTIONS.OFFERS_AND_REWARDS.PUBLIC;
    const offersPrivatePrefix = API_ROUTES.PROMOTIONS.OFFERS_AND_REWARDS.PRIVATE.split('?')[0] ?? API_ROUTES.PROMOTIONS.OFFERS_AND_REWARDS.PRIVATE;

    void invalidateQueriesMatchingPath(offersPublicPrefix);
    void invalidateQueriesMatchingPath(offersPrivatePrefix);
    void invalidateQueriesMatchingPath(bonusFundsPrefix);
    void invalidateQueriesMatchingPath(freeSpinsPrefix);
  }, [hasNotifications, invalidateQueriesMatchingPath]);

  useEffect(() => {
    if (!hasNotifications) {
      hasInvalidatedRef.current = false;
    }
  }, [hasNotifications]);

  const optionsWithNotifications = options.map(option => {
    const notificationType = Object.keys(NOTIFICATION_TYPE_TO_TAB_VALUE).find(type => NOTIFICATION_TYPE_TO_TAB_VALUE[type] === option.value);

    const hasNotifications = notificationType ? hasNotificationsForType(notificationType) : false;

    return {
      ...option,
      showNotification: hasNotifications,
    };
  });

  const enrichedPromoType = optionsWithNotifications.find(opt => opt.value === promoType.value) || optionsWithNotifications[0]!;

  const handlePromoChange = (option: Option<PromotionsPageType>) => {
    const previousTabValue = promoType.value;
    const previousItemType = TAB_VALUE_TO_NOTIFICATION_TYPE[previousTabValue];
    if (previousItemType && hasNotificationsForType(previousItemType)) {
      clearMarkedNotificationsForType(previousItemType);
    }
    setPromoType(option);
  };

  const content = { namespace: 'offers.page', className: 'll-container-inner py-4 sm:py-6' };

  const promotionComponents: Record<PromotionsPageType, React.ReactNode> = {
    offers: <PromoContentOffers className={content.className} showFilters={showFilters} filteredVertical={promoType.filteredVertical ?? []} />,
    bonuses: <PromoContentBonusFunds className={content.className} namespace={content.namespace} filteredVertical={promoType.filteredVertical ?? []} />,
    'free-spins': <PromoContentFreeSpins className={content.className} namespace={content.namespace} />,
    'free-bets': <SportsbookFreeBets className={content.className} />,
  };

  return (
    <>
      {showTabSwitcher ? (
        <PromotionsHeader variant={headerVariant}>
          <ContentSwitcher<PromotionsPageType> id="offers-group" size="large" value={enrichedPromoType} options={optionsWithNotifications} onChange={handlePromoChange} />
        </PromotionsHeader>
      ) : null}
      <PromotionsLoginOrSignUp isProtectedRoute={loginProtectionRequired} route={promoType.value}>
        <PromotionsFilterProvider>
          {showFilters && promoType.value === 'offers' ? <FiltersPanel /> : null}
          {promotionComponents[promoType.value]}
        </PromotionsFilterProvider>
      </PromotionsLoginOrSignUp>
    </>
  );
}

export { FiltersPanel, PromotionsPageClient, type PromotionsPageType };
